import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe, DecimalPipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TripResponse } from 'lcmm-lib-js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface CertificateData {
  certificateId: string;
  issuerCompanyName: string;
  companyName: string;
  savedCo2: string;
  unit: string;
  periodFrom: string;
  periodTo: string;
  issueDate: string;
  validityDate: string;
  calculationDate: string;
  monetaryEquivalent: string;
  distanceTraveled: string;
  gco2PerTkm: string;
  issuerAddress: string;
  issuerZip: string;
  issuerCity: string;
  issuerEmail: string;
  issuerWebsite: string;
  issuerName: string;
  signature?: string;
  verificationCode: string;
}

export interface PdfGenerationOptions {
  filename?: string;
}

export interface PdfGenerationResult {
  success: boolean;
  filename: string;
  size?: number;
  error?: string;
  certificateId: string;
  generatedAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class CertificateService {
  private templateCache: string | null = null;

  private readonly templatePath = 'assets/co2-certificate-template.html';

  private isGenerating = false;

  private defaultIssuerInfo = {
    issuerCompanyName: 'T-Systems International GmbH',
    issuerName: 'Low Carbon Mobility Management',
    issuerAddress: 'Hahnstraße 43d',
    issuerZip: '60528',
    issuerCity: 'Frankfurt am Main',
    issuerEmail: 'info@t-systems.com',
    issuerWebsite: 'https://www.t-systems.com/',
  };

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe
  ) {}

  public async generateCertificate(
    trip: TripResponse,
    customFilename?: string
  ): Promise<boolean> {
    if (!trip) {
      return false;
    }

    try {
      const certificateData = this.prepareCertificateDataFromTrip(trip);
      const options: PdfGenerationOptions = customFilename
        ? { filename: customFilename }
        : {};
      const result = await this.generateCertificatePDF(
        certificateData,
        options
      );

      if (result.success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate certificate as Base64 string
   */
  public async getCertificateAsBase64(
    trip: TripResponse
  ): Promise<string | null> {
    if (!trip) {
      return null;
    }

    try {
      const certificateData = this.prepareCertificateDataFromTrip(trip);
      return await this.getCertificateDataAsBase64(certificateData);
    } catch (error) {
      return null;
    }
  }

  private async generateCertificatePDF(
    certificateData: CertificateData,
    options: PdfGenerationOptions = {}
  ): Promise<PdfGenerationResult> {
    if (this.isGenerating) {
      return {
        success: false,
        filename: '',
        error:
          'PDF generation already in progress. Please wait for current generation to complete.',
        certificateId: certificateData.certificateId,
        generatedAt: new Date(),
      };
    }

    this.isGenerating = true;

    try {
      this.validateCertificateData(certificateData);
      const htmlContent = await this.prepareTemplate(certificateData);
      const pdfBlob = await this.generatePDF(htmlContent);

      const filename =
        options.filename || this.generateFilename(certificateData);
      this.downloadBlob(pdfBlob, filename);

      return {
        success: true,
        filename,
        size: pdfBlob.size,
        certificateId: certificateData.certificateId,
        generatedAt: new Date(),
      };
    } catch (error) {
      return {
        success: false,
        filename: '',
        error: error instanceof Error ? error.message : 'Unknown error',
        certificateId: certificateData.certificateId,
        generatedAt: new Date(),
      };
    } finally {
      this.isGenerating = false;
    }
  }

  private async getCertificateDataAsBase64(
    certificateData: CertificateData
  ): Promise<string> {
    if (this.isGenerating) {
      throw new Error(
        'PDF generation in progress. Please wait before generating base64.'
      );
    }

    this.isGenerating = true;

    try {
      const htmlContent = await this.prepareTemplate(certificateData);
      const pdfBlob = await this.generatePDF(htmlContent);

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => {
          reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(pdfBlob);
      });
    } catch (error) {
      throw new Error(`Failed to generate base64 PDF: ${error}`);
    } finally {
      this.isGenerating = false;
    }
  }

  private async prepareTemplate(
    certificateData: CertificateData
  ): Promise<string> {
    const template = await this.loadTemplate();
    return this.populateTemplate(template, certificateData);
  }

  private async loadTemplate(): Promise<string> {
    if (this.templateCache) {
      return this.templateCache;
    }

    try {
      this.templateCache = await firstValueFrom(
        this.http.get(this.templatePath, { responseType: 'text' })
      );
      return this.templateCache;
    } catch (error) {
      throw new Error(
        `Failed to load template from ${this.templatePath}: ${error}`
      );
    }
  }

  private populateTemplate(template: string, data: CertificateData): string {
    const replacements: Record<string, string> = {
      '{{certificateId}}': data.certificateId,
      '{{issuerCompanyName}}': data.issuerCompanyName,
      '{{companyName}}': data.companyName,
      '{{savedCo2}}': data.savedCo2,
      '{{unit}}': data.unit,
      '{{periodFrom}}': data.periodFrom,
      '{{periodTo}}': data.periodTo,
      '{{issueDate}}': data.issueDate,
      '{{validityDate}}': data.validityDate,
      '{{calculationDate}}': data.calculationDate,
      '{{monetaryEquivalent}}': data.monetaryEquivalent,
      '{{distanceTraveled}}': data.distanceTraveled,
      '{{gco2PerTkm}}': data.gco2PerTkm,
      '{{issuerAddress}}': data.issuerAddress,
      '{{issuerZip}}': data.issuerZip,
      '{{issuerCity}}': data.issuerCity,
      '{{issuerEmail}}': data.issuerEmail,
      '{{issuerWebsite}}': data.issuerWebsite,
      '{{issuerName}}': data.issuerName,
      '{{signature}}': data.signature || '',
      '{{verificationCode}}': data.verificationCode,
    };

    let populated = template;
    Object.entries(replacements).forEach(([key, value]) => {
      const regex = new RegExp(key.replace(/[{}]/g, '\\$&'), 'g');
      populated = populated.replace(regex, value);
    });

    return populated;
  }

  private async generatePDF(htmlContent: string): Promise<Blob> {
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position: absolute; left: -9999px; width: 210mm; height: 297mm; border: none;';
    document.body.appendChild(iframe);

    try {
      const doc = iframe.contentDocument;
      if (!doc) {
        throw new Error('Failed to access iframe document');
      }

      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            @page { margin: 0; size: A4; }
            body { margin: 0; font-family: Arial, sans-serif; }
            * { 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact;
              color-adjust: exact;
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
            }
          </style>
        </head>
        <body>${htmlContent}</body>
        </html>
      `);
      doc.close();

      await this.delay(2000);

      const canvas = await html2canvas(doc.body, {
        scale: 4,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
        logging: false,
        imageTimeout: 10000,
        removeContainer: true,
        foreignObjectRendering: true,
        onclone: (clonedDoc) => {
          const elements = clonedDoc.querySelectorAll('*');
          elements.forEach((element: Element) => {
            const el = element as HTMLElement;
            const newStyle = {
              webkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              colorAdjust: 'exact',
              textRendering: 'optimizeLegibility',
              fontSmooth: 'always',
              webkitFontSmoothing: 'antialiased',
            };
            Object.assign(el.style, newStyle);
          });
        },
      });

      // eslint-disable-next-line new-cap
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: false,
        precision: 16,
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', 0, 0, 595.28, 841.89, undefined, 'FAST');

      pdf.setProperties({
        title: `CO2 Certificate - ${this.extractCertificateId(htmlContent)}`,
        subject: 'Carbon Reduction Certificate',
        author: 'T-Systems International GmbH',
        creator: 'Low Carbon Mobility Management',
      });

      return new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
    } finally {
      document.body.removeChild(iframe);
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private generateFilename(data: CertificateData): string {
    const safeName = data.companyName.replace(/[^a-zA-Z0-9]/g, '-');
    const date = new Date().toISOString().split('T')[0];
    return `${safeName}-CO2-Certificate-${date}.pdf`;
  }

  private extractCertificateId(htmlContent: string): string {
    const match = htmlContent.match(/Certificate No:\s*([^<]+)/);
    return match ? match[1].trim() : 'Unknown';
  }

  private validateCertificateData(data: CertificateData): void {
    const required = [
      'certificateId',
      'companyName',
      'issuerCompanyName',
      'savedCo2',
    ];
    const missing = required.filter(
      (field) => !data[field as keyof CertificateData]
    );

    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, ms);
    });
  }

  public clearCache(): void {
    this.templateCache = null;
  }

  private prepareCertificateDataFromTrip(trip: TripResponse): CertificateData {
    const issuer = { ...this.defaultIssuerInfo };
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(today.getFullYear() + 1);

    return {
      certificateId: this.generateCertificateId(trip.id),
      issuerCompanyName: issuer.issuerCompanyName,
      companyName: trip.vehicle._name,
      savedCo2:
        this.decimalPipe.transform(
          ((trip.absoluteCalculation.percentageWorkCycle - 1) *
            trip.absoluteCalculation.co2Emission) /
            trip.absoluteCalculation.percentageWorkCycle,
          '1.0-1'
        ) || '0.0',
      unit: 'kg',
      periodFrom: this.datePipe.transform(trip.startTime, 'dd.MM.yyyy') || '',
      periodTo: this.datePipe.transform(trip.endTime, 'dd.MM.yyyy') || '',
      issueDate: this.datePipe.transform(today, 'dd.MM.yyyy') || '',
      validityDate: this.datePipe.transform(nextYear, 'yyyy') || '',
      calculationDate: this.datePipe.transform(today, 'dd.MM.yyyy') || '',
      monetaryEquivalent:
        this.decimalPipe.transform(
          Math.abs(
            (((trip.absoluteCalculation.percentageWorkCycle - 1) *
              trip.absoluteCalculation.co2Emission) /
              trip.absoluteCalculation.percentageWorkCycle /
              1000) *
              55
          ),
          '1.0-2'
        ) || '0.0',
      distanceTraveled:
        this.decimalPipe.transform(trip.calculation.distance, '1.0-1') || '0.0',
      gco2PerTkm:
        this.decimalPipe.transform(
          trip.absoluteCalculation.co2Emission /
            (trip.calculation.distance / 1000) /
            (trip.vehicle.mass / 1000),
          '1.0-1'
        ) || '0.0',
      issuerAddress: issuer.issuerAddress,
      issuerZip: issuer.issuerZip,
      issuerCity: issuer.issuerCity,
      issuerEmail: issuer.issuerEmail,
      issuerWebsite: issuer.issuerWebsite,
      issuerName: issuer.issuerName,
      signature: '',
      verificationCode: this.generateVerificationCode(trip.id),
    };
  }

  private generateVerificationCode(tripId: string): string {
    const hash = this.simpleHash(tripId + new Date().toDateString());
    return hash.substring(0, 12).toUpperCase();
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
      const char = str.charCodeAt(i);
      hash = hash * 32 - hash + char;
      hash &= hash; // eslint-disable-line no-bitwise
    }
    return Math.abs(hash).toString(36);
  }

  private generateCertificateId(tripId: string): string {
    const timestamp = Date.now();
    const randomSuffix = this.generateSecureRandom();
    const hash = this.createHash(`${tripId}-${timestamp}-${randomSuffix}`);

    const year = new Date().getFullYear();
    return `CERT-${year}-${hash.substring(0, 10).toUpperCase()}`;
  }

  private generateSecureRandom(): string {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint32Array(2);
      crypto.getRandomValues(array);
      return array[0].toString(36) + array[1].toString(36);
    }

    const random1 = Math.random().toString(36).substring(2);
    const random2 = Math.random().toString(36).substring(2);
    const timeEntropy = Date.now().toString(36);
    return random1 + random2 + timeEntropy;
  }

  private createHash(input: string): string {
    let hash = 0;
    const saltedInput = `${input}-CERT-SALT-${Date.now()}`;

    for (let i = 0; i < saltedInput.length; i += 1) {
      const char = saltedInput.charCodeAt(i);
      hash = hash * 32 - hash + char;
      hash &= hash; // eslint-disable-line no-bitwise
      hash = Math.abs(hash);
    }

    const hashString = hash.toString(36);
    return hashString.padStart(8, '0');
  }
}
